import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import DateSelect from "@/components/DateSelect";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import useModal from "@/hooks/useModal";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import pluralize from "@/lib/pluralize";
import { DateTime } from "luxon";
import { useCommercePayorNotificationEmailPreview } from "@/pages/billing/_hooks/useCommercePayorNotificationEmailPreview";
import { CommercePayorNotificationEmailType } from "@/pages/billing/_queries/emailPreviewQueries";

export type ChangeDueDateModalProps = {
  onDismiss: () => void;
  orders: Record<string, boolean>;
  onSubmit: (variables: any, close: () => void, sendNotification?: boolean) => void;
  loading: boolean;
  errorContent: string;
};

export const ChangeDueDateModal = ({ onDismiss, orders, onSubmit, loading, errorContent }: ChangeDueDateModalProps) => {
  const { control, watch } = useForm();
  const orderIds = Object.keys(orders);
  const previewOrderId = orderIds[0] ?? "";
  const headline = `Change Due ${pluralize(orderIds.length, "Date")}`;
  const { close, Modal } = useModal();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useCommercePayorNotificationEmailPreview(previewOrderId);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const dueDate = DateTime.now().plus({ days: 30 }).toISO();

  const orderDueDateRange = watch("orderDueDate", {
    startDate: dueDate,
    endDate: dueDate
  });

  const variables = {
    orderIds: orderIds,
    dueDate: orderDueDateRange.startDate,
    sendNotification: sendNotification
  };

  const handlePreviewEmail = () => {
    if (!previewOrderId) {
      return;
    }

    setPreviewOpen(true);
    void loadPreview({
      notificationType: CommercePayorNotificationEmailType.INVOICE_AVAILABLE,
      dueDate: orderDueDateRange.startDate
    });
  };

  return (
    <>
      <Modal
        onClosed={onDismiss}
        actions={
          <EmailPreviewModalActions
            onPreview={handlePreviewEmail}
            previewDisabled={loading || !sendNotification || !previewOrderId}
          >
            <Button variant="text" onClick={close} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={() => onSubmit(variables, close, sendNotification)}
              disabled={loading || !orderDueDateRange.startDate}
            >
              {headline}
            </Button>
          </EmailPreviewModalActions>
        }
        headline={headline}
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} mt-[200px]`}
        displayOverflow
      >
        {errorContent ? <div className="text-red-500 font-bold text-center mb-4">{errorContent}</div> : null}
        <p>
          Changing the due {pluralize(orderIds.length, "date")} of the selected {pluralize(orderIds.length, "order")}{" "}
          may affect eligibility for suspension or termination.
        </p>
        <p className="mt-5 text-label-large">Orders to Update: {orderIds.length}</p>
        <div className="mt-6 flex flex-col gap-6">
          <input className="mt-4 ml-1 absolute"></input>

          <Controller
            name="orderDueDate"
            control={control}
            render={({ field }) => (
              <DateSelect
                label="Order Due Date *"
                useModal={true}
                value={orderDueDateRange}
                onChange={(e) => {
                  field.onChange(e);
                }}
                useSingleDate
                showFooter={false}
              />
            )}
          />

          <div>
            <Checkbox
              name="send-notification"
              label="Send email notification to payor"
              checked={sendNotification}
              onChange={() => {
                setSendNotification(!sendNotification);
              }}
            />
          </div>
        </div>
      </Modal>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
};
